import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useMirrorAdminAllCompaniesQuery,
  useMirrorTargetCompaniesQuery,
  useSetMirrorTargetCompaniesMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";

type CompanyOption = { uuid: string; name: string };

/**
 * Dialog for choosing which companies a target mirrors via the live cron: a
 * single multi-select. Saving replaces the full list (diff-based on the
 * backend). This is the per-target cron selection only; on-demand sync can
 * request any company regardless of this list.
 *
 * No target-side UUID mapping is collected — lower environments are full
 * backups of production, so company UUIDs are identical across environments.
 */
const MirrorTargetCompaniesEditor = ({
  targetUuid,
  onClose,
}: {
  readonly targetUuid: string;
  readonly onClose: () => void;
}) => {
  const { data, error } = useMirrorTargetCompaniesQuery({
    variables: { targetUuid },
    fetchPolicy: "network-only",
  });
  const { data: companiesData } = useMirrorAdminAllCompaniesQuery();
  const [setMirrorTargetCompanies, { loading: saving }] =
    useSetMirrorTargetCompaniesMutation();

  const [selected, setSelected] = useState<CompanyOption[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (data !== undefined) {
      setSelected(
        data.mirrorTargetCompanies.map((row) => ({
          uuid: row.companyUuid,
          name: row.companyName,
        })),
      );
    }
  }, [data]);

  const companyOptions = useMemo(
    () =>
      (companiesData?.companies ?? []).map((c) => ({
        uuid: c.uuid,
        name: c.name,
      })),
    [companiesData],
  );

  const save = useCallback(async () => {
    setSaveError(null);
    try {
      const result = await setMirrorTargetCompanies({
        variables: {
          input: {
            targetUuid,
            companyUuids: selected.map((c) => c.uuid),
          },
        },
      });
      throwOnGraphqlErrors(result);
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Save failed");
    }
  }, [onClose, selected, setMirrorTargetCompanies, targetUuid]);

  return (
    <Dialog fullWidth open maxWidth="md" onClose={onClose}>
      <DialogTitle>Companies to mirror</DialogTitle>
      <DialogContent>
        {error !== undefined && <Alert severity="error">{error.message}</Alert>}
        {saveError !== null && <Alert severity="error">{saveError}</Alert>}
        <Autocomplete
          multiple
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
          options={companyOptions}
          renderInput={(params) => (
            <TextField {...params} label="Companies" margin="normal" />
          )}
          size="small"
          value={selected}
          onChange={(_, value) => {
            setSelected(value);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1}>
          <Button
            disabled={saving}
            variant="contained"
            onClick={() => {
              void save();
            }}
          >
            Save
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default MirrorTargetCompaniesEditor;
